import { reportError } from "src/lib/observability/reportError";
import { useCallback, useEffect, useState } from "react";
import {
	FirebaseAPI,
	getCartCost,
	NewOrganizationSchema,
	TCategory,
	TDiscount,
	TOrganization,
	TOrganizationGroup,
	TNewOrganizationGroup,
	NewOrganizationGroupSchema,
	TSupplier,
	TNewSupplier,
	NewSupplierSchema,
	SupplierSchema,
	TSupplierInvoice,
	TCreditNote,
	TStoreDocument,
	TDeliveryNoteDocument,
} from "@jsdev_ninja/core";
import { TOrder } from "@jsdev_ninja/core";
import { useStoreActions } from "src/infra";
import { FirebaseApi } from "src/lib/firebase";
import { mixPanelApi } from "src/lib/mixpanel";
import { SentryApi } from "src/lib/sentry";
import { SubNestedKeys } from "src/shared/types";
import { productCreate, productSave } from "./admin";
import {
	ProductSchema,
	TFavoriteProduct,
	TNewProduct,
	TProduct,
	TProfile,
	TNewOrganization,
	TAddress,
} from "@jsdev_ninja/core";

import { TCart } from "src/domains/cart";
import { navigate } from "src/navigation";
import { TStoreStats } from "src/types";
import { LogPayload } from "src/lib/firebase/api";
import { useApiState } from "./useApiState";
import { modalApi } from "src/infra/modals";
import diff from "microdiff";
import { removeUndefinedFields } from "src/utils/removeUndefinedFields";

function productInCart(cart: TCart | null, product: TProduct) {
	return !!cart?.items?.find((item) => item.product.id === product.id);
}

export const useAppApi = () => {
	const { appReady, cart, company, store, user, profile } = useApiState();
	const actions = useStoreActions();

	const cartId = cart?.id ?? FirebaseApi.firestore.generateDocId("cart");

	const storeId = store?.id;
	const tenantId = store?.tenantId;
	const companyId = store?.companyId;
	const userId = user?.uid;

	const [loading, setLoading] = useState<
		Partial<
			Record<SubNestedKeys<Omit<typeof api, "loading">>, boolean | undefined>
		>
	>({});

	function updateLoading(
		update: Partial<
			Record<SubNestedKeys<Omit<typeof api, "loading">>, boolean | undefined>
		>,
	) {
		setLoading({ ...loading, ...update });
	}

	const isValidStoreData = companyId && storeId && tenantId;
	const isValidUser = !!user && userId && isValidStoreData;

	const isValid = !!company?.id && store?.id && !!user?.uid;
	const isValidAdmin = !!companyId && !!storeId && !!user?.uid && !!user.admin;

	const allowAnonymousClients = !!store?.allowAnonymousClients;

	const actualCart: TCart = cart ?? {
		id: cartId,
		companyId: store?.companyId ?? "",
		storeId: store?.id ?? "",
		userId: user?.uid ?? "",
		items: [],
		type: "Cart",
		status: "active",
	};

	const logger = useCallback(
		(
			payload: {
				message: LogPayload["message"];
				severity: LogPayload["severity"];
			} & {
				[key: string]: any;
			},
		) => {
			FirebaseApi.api.uiLogs({
				companyId,
				storeId,
				tenantId,
				userId,
				url: window.location.href,
				...payload,
			});
		},
		[companyId, storeId, tenantId, userId],
	);
	useEffect(() => {
		if (appReady && !isValidStoreData) {
			logger({
				message: "invalid store data",
				severity: "ALERT",
			});
		}
	}, [appReady, isValidStoreData, logger]);

	const api = {
		logger,
		orders: {
			list: async () => {
				if (!isValidStoreData) return;
				const res = await FirebaseApi.firestore.listV2<TOrder>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "orders",
						storeId,
						companyId,
					}),
					sort: [{ name: "date", value: "desc" }],
					where: [{ name: "storeId", operator: "==", value: store.id }],
				});

				return res;
			},
			order: async ({ order }: { order: TOrder }) => {
				if (!isValidUser) return;

				const res = await FirebaseApi.api.createOrder({
					order,
					companyId,
					storeId,
				});

				if (!res.success) {
					reportError("Failed to create order via API:", res.error);
					return null; // maintaining legacy behavior of returning falsy or throwing
				}

				return res;
			},
		},
		contactForm: {
			submit: async (data: {
				name: string;
				email: string;
				company: string;
				phone?: string;
				message?: string;
			}) => {
				if (!isValidStoreData) return { success: false, docId: "" };

				const doc = {
					...data,
					createdAt: Date.now(),
				};
				return await FirebaseApi.firestore.createV2<
					typeof doc & { id?: string }
				>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "contactSubmissions",
						companyId,
						storeId,
					}),
					doc,
				});
			},
		},
		admin: {
			uploadSupplierInvoice: async (invoice: TSupplierInvoice) => {
				if (!isValidAdmin) return;

				return await FirebaseApi.firestore.createV2({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "supplierInvoices",
						companyId,
						storeId,
					}),
					doc: invoice,
					id: invoice.id,
				});
			},
			listSupplierInvoices: async () => {
				if (!isValidAdmin || !companyId || !storeId) return;

				updateLoading({ "admin.listSupplierInvoices": true });
				const result = await FirebaseApi.firestore.listV2<TSupplierInvoice>(
					{
						collection: FirebaseAPI.firestore.getPath({
							storeId,
							companyId,
							collectionName: "supplierInvoices",
						}),
						sort: [{ name: "date", value: "desc" }],
					},
				);
				updateLoading({ "admin.listSupplierInvoices": false });

				// Exclude drafts from the finalized list. Legacy invoices have no
				// status and are treated as finalized.
				if (result?.success && Array.isArray(result.data)) {
					return {
						...result,
						data: result.data.filter(
							(invoice) => invoice.status !== "draft",
						),
					};
				}

				return result;
			},
			// Save a supplier invoice as a draft (work-in-progress). Stored in the
			// same `supplierInvoices` collection with status "draft" so the existing
			// permissions apply; the create trigger skips drafts, so NO product
			// prices are updated until the invoice is finalized.
			saveSupplierInvoiceDraft: async (
				draft: Partial<TSupplierInvoice> & { id: string },
			) => {
				if (!isValidAdmin || !companyId || !storeId) return;

				const { supplier, ...rest } = draft;
				return await FirebaseApi.firestore.setV2({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "supplierInvoices",
						companyId,
						storeId,
					}),
					doc: {
						...rest,
						// only include supplier when selected (avoid undefined writes)
						...(supplier ? { supplier } : {}),
						type: "SupplierInvoice",
						status: "draft",
					},
				});
			},
			listSupplierInvoiceDrafts: async () => {
				if (!isValidAdmin || !companyId || !storeId) return;

				updateLoading({ "admin.listSupplierInvoiceDrafts": true });
				// Filter by status only (no orderBy) so the query does NOT require a
				// composite Firestore index — otherwise it fails and drafts appear
				// to "not save". Drafts are sorted by date (newest first) client-side.
				const result = await FirebaseApi.firestore.listV2<TSupplierInvoice>(
					{
						collection: FirebaseAPI.firestore.getPath({
							storeId,
							companyId,
							collectionName: "supplierInvoices",
						}),
						where: [{ name: "status", value: "draft", operator: "==" }],
					},
				);
				updateLoading({ "admin.listSupplierInvoiceDrafts": false });

				if (result?.success && Array.isArray(result.data)) {
					return {
						...result,
						data: [...result.data].sort(
							(a, b) => (b.date ?? 0) - (a.date ?? 0),
						),
					};
				}

				return result;
			},
			deleteSupplierInvoiceDraft: async (id: string) => {
				if (!isValidAdmin || !companyId || !storeId) return;

				return await FirebaseApi.firestore.remove({
					id,
					collectionName: FirebaseAPI.firestore.getPath({
						collectionName: "supplierInvoices",
						companyId,
						storeId,
					}),
				});
			},
			getSupplierInvoice: async (id: string) => {
				if (!isValidAdmin || !companyId || !storeId) return;

				updateLoading({ "admin.getSupplierInvoice": true });
				const result = await FirebaseApi.firestore.getV2<TSupplierInvoice>({
					collection: FirebaseAPI.firestore.getPath({
						storeId,
						companyId,
						collectionName: "supplierInvoices",
					}),
					id,
				});
				updateLoading({ "admin.getSupplierInvoice": false });

				return result;
			},
			// Documents (finalized AND drafts) that already use this invoice number,
			// so the create form can warn about a duplicate before the same
			// delivery note is entered twice. Exact match on the trimmed number;
			// a single equality filter needs no composite Firestore index.
			// Deliberately does NOT toggle the global loading state — it runs while
			// the user types.
			findSupplierInvoicesByNumber: async (invoiceNumber: string) => {
				if (!isValidAdmin || !companyId || !storeId) return;

				const trimmed = invoiceNumber?.trim();
				if (!trimmed) return { success: true, data: [] as TSupplierInvoice[] };

				return await FirebaseApi.firestore.listV2<TSupplierInvoice>({
					collection: FirebaseAPI.firestore.getPath({
						storeId,
						companyId,
						collectionName: "supplierInvoices",
					}),
					where: [{ name: "invoiceNumber", value: trimmed, operator: "==" }],
					limitCount: 20,
				});
			},
			deleteDiscount: async (id: string) => {
				if (!isValidAdmin) return;

				return await FirebaseApi.firestore.remove({
					id,
					collectionName: FirebaseAPI.firestore.getPath({
						collectionName: "discounts",
						companyId,
						storeId,
					}),
				});
			},
			createDiscount: async (discount: TDiscount) => {
				if (!isValidAdmin) return;

				const res = await FirebaseApi.firestore.createV2<TDiscount>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "discounts",
						companyId,
						storeId,
					}),
					doc: discount,
				});

				logger({
					message: "create discount",
					severity: res.success ? "INFO" : "ERROR",
					res,
					discount,
				});

				return;
			},
			subscribeToDiscounts: (callback: (discounts: TDiscount[]) => void) => {
				if (!isValidAdmin) return () => {};

				return FirebaseApi.firestore.subscribeList<TDiscount>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "discounts",
						companyId,
						storeId,
					}),
					callback,
				});
			},
			getOrder: async (id: string) => {
				if (!isValidAdmin) return;

				return FirebaseApi.firestore.getV2<TOrder>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "orders",
						companyId,
						storeId,
					}),
					id,
				});
			},
			getStoreOrders: async (options?: { limit?: number }) => {
				if (!isValidAdmin) return;

				return FirebaseApi.firestore.listV2<TOrder>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "orders",
						companyId,
						storeId,
					}),
					where: [
						{
							name: "storeId",
							operator: "==",
							value: store.id,
						},
						{
							name: "companyId",
							operator: "==",
							value: companyId,
						},
					],
					sort: [{ name: "date", value: "desc" }],
					...(options?.limit !== undefined
						? { limitCount: options.limit }
						: {}),
				});
			},
			subscribeToOrders: (callback: (orders: TOrder[]) => void) => {
				if (!isValidAdmin) return () => {};

				return FirebaseApi.firestore.subscribeList<TOrder>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "orders",
						companyId,
						storeId,
					}),
					where: [
						{
							name: "storeId",
							operator: "==",
							value: store.id,
						},
						{
							name: "companyId",
							operator: "==",
							value: companyId,
						},
					],
					callback,
				});
			},
			getOrdersForInvoice: async ({
				organizationId,
				billingAccount,
				fromDate,
				toDate,
			}: {
				organizationId: string;
				billingAccount?: string;
				fromDate: number;
				toDate: number;
			}) => {
				if (!isValidAdmin) return;

				const whereClauses = [
					{
						name: "storeId" as const,
						operator: "==" as const,
						value: store.id,
					},
					{
						name: "companyId" as const,
						operator: "==" as const,
						value: companyId,
					},
					{
						name: "organizationId" as const,
						operator: "==" as const,
						value: organizationId,
					},
					{
						name: "ezDeliveryNote.success" as const,
						operator: "==" as const,
						value: true,
					},
					{
						name: "date" as const,
						operator: ">=" as const,
						value: fromDate,
					},
					{
						name: "date" as const,
						operator: "<=" as const,
						value: toDate,
					},
				];

				if (billingAccount) {
					whereClauses.push({
						name: "billingAccount.number" as any,
						operator: "==" as const,
						value: billingAccount,
					});
				}

				return FirebaseApi.firestore.listV2<TOrder>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "orders",
						companyId,
						storeId,
					}),
					where: whereClauses,
					sort: [{ name: "date", value: "desc" }],
				});
			},
			getOrdersForDeliveryNote: async ({
				fromDate,
				toDate,
			}: {
				fromDate: number;
				toDate: number;
			}) => {
				if (!isValidAdmin) return;

				const whereClauses = [
					{
						name: "storeId" as const,
						operator: "==" as const,
						value: store.id,
					},
					{
						name: "companyId" as const,
						operator: "==" as const,
						value: companyId,
					},

					{
						name: "date" as const,
						operator: ">=" as const,
						value: fromDate,
					},
					{
						name: "date" as const,
						operator: "<=" as const,
						value: toDate,
					},
				];

				return FirebaseApi.firestore.listV2<TOrder>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "orders",
						companyId,
						storeId,
					}),
					where: whereClauses,
					sort: [{ name: "date", value: "desc" }],
				});
			},
			getOrganizationOrders: async (organizationId: string) => {
				if (!isValidAdmin) return;

				return FirebaseApi.firestore.listV2<TOrder>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "orders",
						companyId,
						storeId,
					}),
					where: [
						{
							name: "storeId" as const,
							operator: "==" as const,
							value: store.id,
						},
						{
							name: "companyId" as const,
							operator: "==" as const,
							value: companyId,
						},
						{
							name: "organizationId" as const,
							operator: "==" as const,
							value: organizationId,
						},
					],
					sort: [{ name: "date", value: "desc" }],
				});
			},
			getOrganizationInvoices: async (organizationId: string) => {
				if (!isValidAdmin) return;

				return FirebaseApi.firestore.listV2<TOrder>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "orders",
						companyId,
						storeId,
					}),
					where: [
						{
							name: "storeId" as const,
							operator: "==" as const,
							value: store.id,
						},
						{
							name: "companyId" as const,
							operator: "==" as const,
							value: companyId,
						},
						{
							name: "organizationId" as const,
							operator: "==" as const,
							value: organizationId,
						},
						{
							name: "invoice" as const,
							operator: "!=" as const,
							value: null,
						},
					],
					sort: [{ name: "date", value: "desc" }],
				});
			},
			getDeliveryNotes: async ({
				fromDate,
				toDate,
			}: {
				fromDate: number;
				toDate: number;
			}) => {
				if (!isValidAdmin) return;

				return FirebaseApi.firestore.listV2<TOrder>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "orders",
						companyId,
						storeId,
					}),
					where: [
						{
							name: "storeId" as const,
							operator: "==" as const,
							value: store.id,
						},
						{
							name: "companyId" as const,
							operator: "==" as const,
							value: companyId,
						},
						{
							name: "ezDeliveryNote.success" as const,
							operator: "==" as const,
							value: true,
						},
						{
							name: "date" as const,
							operator: ">=" as const,
							value: fromDate,
						},
						{
							name: "date" as const,
							operator: "<=" as const,
							value: toDate,
						},
					],
					sort: [{ name: "date", value: "desc" }],
				});
			},
			/**
			 * List all orders with a successful invoice for the current store within
			 * the given date range. Mirrors getDeliveryNotes — swaps ezDeliveryNote.success
			 * → invoice.success and filters by order date (epoch millis).
			 */
			getInvoices: async ({
				fromDate,
				toDate,
			}: {
				fromDate: number;
				toDate: number;
			}) => {
				if (!isValidAdmin) return;

				return FirebaseApi.firestore.listV2<TOrder>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "orders",
						companyId,
						storeId,
					}),
					where: [
						{
							name: "storeId" as const,
							operator: "==" as const,
							value: store.id,
						},
						{
							name: "companyId" as const,
							operator: "==" as const,
							value: companyId,
						},
						{
							name: "invoice.success" as const,
							operator: "==" as const,
							value: true,
						},
						{
							name: "date" as const,
							operator: ">=" as const,
							value: fromDate,
						},
						{
							name: "date" as const,
							operator: "<=" as const,
							value: toDate,
						},
					],
					sort: [{ name: "date", value: "desc" }],
				});
			},
			/**
			 * Business-document RECORDS (documents collection) in an issue-date range.
			 *
			 * ONE record per issued document — an invoice covering N orders appears
			 * once, which is what makes it the right source for the invoices screen
			 * (the per-order scan above shows a consolidated invoice N times).
			 *
			 * Returns ALL document types in range (delivery notes, invoices,
			 * receipts); callers filter by `type`. The range is deliberately the only
			 * query constraint: an equality-on-type + range-on-date query needs a
			 * composite Firestore index this project does not manage, while a
			 * single-field range is indexed automatically. Monthly volume is a few
			 * hundred docs at most.
			 */
			getStoreDocuments: async ({
				fromDate,
				toDate,
			}: {
				fromDate: number;
				toDate: number;
			}) => {
				if (!isValidAdmin) return;

				return FirebaseApi.firestore.listV2<TStoreDocument>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "documents",
						companyId,
						storeId,
					}),
					where: [
						{
							name: "issueDate" as const,
							operator: ">=" as const,
							value: fromDate,
						},
						{
							name: "issueDate" as const,
							operator: "<=" as const,
							value: toDate,
						},
					],
					sort: [{ name: "issueDate", value: "desc" }],
					limitCount: 1000,
				});
			},
			/**
			 * Every delivery note that is still OPEN — issued, not yet consolidated
			 * into an invoice, not cancelled, not currently being invoiced. Read from
			 * the documents collection, which owns the delivery-note lifecycle.
			 * Deliberately NOT bound to a month: a note stays here until it is
			 * invoiced, however long ago it was issued.
			 */
			getOpenDeliveryNotes: async () => {
				if (!isValidAdmin) return;

				const result = await FirebaseApi.firestore.listV2<TDeliveryNoteDocument>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "documents",
						companyId,
						storeId,
					}),
					// Equality-only filters need no composite index; sorted client-side.
					where: [
						{ name: "type" as const, operator: "==" as const, value: "delivery_note" },
						{ name: "status" as const, operator: "==" as const, value: "open" },
					],
					limitCount: 1000,
				});
				if (!result.success) return result;
				return {
					...result,
					data: [...result.data].sort((a, b) => b.issueDate - a.issueDate),
				};
			},
			/**
			 * Load orders by id (e.g. the orders a set of delivery notes cover).
			 * Ids that no longer resolve are skipped.
			 */
			getOrdersByIds: async (ids: string[]) => {
				if (!isValidAdmin) return;

				const ordersPath = FirebaseAPI.firestore.getPath({
					collectionName: "orders",
					companyId,
					storeId,
				});
				const unique = Array.from(new Set(ids));
				const orders: TOrder[] = [];
				const CHUNK = 20;
				for (let i = 0; i < unique.length; i += CHUNK) {
					const results = await Promise.all(
						unique
							.slice(i, i + CHUNK)
							.map((id) => FirebaseApi.firestore.getV2<TOrder>({ collection: ordersPath, id })),
					);
					for (const r of results) {
						if (r.success && r.data) orders.push(r.data);
					}
				}
				return { success: true as const, data: orders };
			},
			/**
			 * List all open (unpaid) invoices for the current store.
			 * Each row is an order that has an EZcount invoice and no recorded payment.
			 * Auth guard is enforced server-side (admin custom claim).
			 */
			getOpenInvoices: async (params?: {
				fromDate?: number;
				toDate?: number;
			}) => {
				if (!isValidAdmin) return;
				const { api } = await import("src/lib/firebase/api");
				return await api.getOpenInvoices(params);
			},

			/**
			 * Admin: AR rollup + entry ledger for one organization (customer ledger card).
			 * Read-only. Auth guard + tenant scope enforced server-side (admin custom claim).
			 */
			getOrganizationBalance: async (params: {
				organizationId: string;
				fromMillis?: number;
				toMillis?: number;
			}) => {
				if (!isValidAdmin) return;
				const { api } = await import("src/lib/firebase/api");
				return await api.getOrganizationBalance(params);
			},

			/**
			 * Record a full payment against an open invoice.
			 * Creates an EZcount receipt (doc_type 400) and marks the invoice paid on the order.
			 * Partial payments are NOT supported — always pays the full invoice total.
			 * Auth guard is enforced server-side (admin custom claim).
			 */
			recordInvoicePayment: async (params: {
				orderId: string;
				paymentMethod: "cash" | "check" | "bank_transfer" | "credit_card";
				paymentDate: number;
				note?: string;
				idempotencyKey: string;
			}) => {
				if (!isValidAdmin) return;
				const { api } = await import("src/lib/firebase/api");
				return await api.recordInvoicePayment(params);
			},

			/**
			 * Issue a credit note (תעודת זיכוי) against an organization's AR balance.
			 * Optionally linked to an invoice (orderId). Creates an EZcount credit
			 * invoice (doc_type 330) and writes a settlement entry to the AR ledger.
			 * Auth guard is enforced server-side (admin custom claim).
			 */
			createCreditNote: async (params: {
				organizationId: string;
				orderId?: string | null;
				amount: number;
				reason: string;
				note?: string | null;
				creditDate: number;
				idempotencyKey: string;
			}) => {
				if (!isValidAdmin) return;
				const { api } = await import("src/lib/firebase/api");
				return await api.createCreditNote(params);
			},

			/**
			 * List all credit notes issued for one organization (tenant-scoped).
			 * Read-only client query — used by the ledger card to show the reason
			 * and PDF link for each זיכוי row.
			 */
			getCreditNotes: async ({ organizationId }: { organizationId: string }) => {
				if (!isValidAdmin || !companyId || !storeId) return;

				return FirebaseApi.firestore.listV2<TCreditNote>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "creditNotes",
						companyId,
						storeId,
					}),
					where: [
						{
							name: "organizationId" as const,
							operator: "==" as const,
							value: organizationId,
						},
					],
					sort: [{ name: "createdAt", value: "desc" }],
				});
			},

			createDeliveryNote: async (
				order: TOrder,
				options?: {
					date?: number;
					sendEmailToClient?: boolean;
					nameOnInvoice?: string;
				},
			) => {
				if (!isValidAdmin) return;

				const { api } = await import("src/lib/firebase/api");
				return await api.createDeliveryNote({ order, options });
			},
			removeProductImage: async ({ product }: { product: TProduct }) => {
				if (!isValidAdmin) return;

				// remove product images
				if (product.images?.[0]) {
					await FirebaseApi.storage.remove(product.images?.[0].url);
				}

				const newProduct = { ...product };
				newProduct.images = [];

				return await FirebaseApi.firestore.setV2({
					doc: removeUndefinedFields(newProduct),
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "products",
						companyId,
						storeId,
					}),
				});
			},
			productCreate: async (newProduct: TNewProduct) => {
				setLoading({ ...loading, "admin.productCreate": true });

				const res = await productCreate(newProduct);
				setLoading({ ...loading, "admin.productCreate": false });
				return res;
			},
			productDelete: async ({ product }: { product: TProduct }) => {
				if (!isValidAdmin) return;

				// remove product images
				if (product.images?.[0]) {
					await FirebaseApi.storage.remove(product.images?.[0].url);
				}

				return await FirebaseApi.firestore.remove({
					id: product.id,
					collectionName: FirebaseAPI.firestore.getPath({
						collectionName: "products",
						companyId,
						storeId,
					}),
				});
			},
			saveProduct: async (newProduct: TNewProduct) => {
				setLoading({ ...loading, "admin.productCreate": true });

				const res = await productSave(removeUndefinedFields(newProduct));
				setLoading({ ...loading, "admin.productCreate": false });
				return res;
			},

			getStoreStats: async () => {
				if (!isValidAdmin) return;
				return FirebaseApi.firestore.getV2<TStoreStats>({
					collection: "store-stats",
					id: store.id,
				});
			},
			getClient: async (clientId: string) => {
				if (!isValidAdmin) return;
				return FirebaseApi.firestore.getV2<TProfile>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "profiles",
						storeId,
						companyId,
					}),
					id: clientId,
				});
			},
			updateClient: async (client: TProfile) => {
				if (!isValidAdmin) return;

				const result = await FirebaseApi.firestore.update<TProfile>(
					client.id,
					client,
					FirebaseAPI.firestore.getPath({
						collectionName: "profiles",
						storeId,
						companyId,
					}),
				);

				logger({
					message: "update client profile",
					severity: result.success ? "INFO" : "ERROR",
					result,
					client,
				});

				return result;
			},
			deleteClient: async (clientId: string) => {
				if (!isValidAdmin) return;

				// Fully delete the client: profile doc + Firebase Auth user. This must
				// run server-side (Admin SDK), so we call the backend callable rather
				// than removing the Firestore doc directly. Org membership lives on the
				// profile, so deleting the profile also clears it.
				const result = await FirebaseApi.api.deleteClient(clientId);

				logger({
					message: "delete client",
					severity: result.success ? "INFO" : "ERROR",
					result,
					clientId,
				});

				return result;
			},
			getStoreClients: async () => {
				if (!isValidAdmin) return;
				return FirebaseApi.firestore.listV2<TProfile>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "profiles",
						storeId,
						companyId,
					}),
					where: [
						{
							name: "storeId",
							operator: "==",
							value: store.id,
						},
					],
				});
			},
			findClientByEmail: async (email: string) => {
				if (!isValidAdmin) return;

				const trimmedEmail = email.trim().toLowerCase();
				if (!trimmedEmail) {
					return { success: false, data: null };
				}

				const result = await FirebaseApi.firestore.listV2<TProfile>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "profiles",
						storeId,
						companyId,
					}),
					where: [
						{
							name: "storeId",
							operator: "==",
							value: store.id,
						},
					],
				});

				if (!result?.success || !result.data) {
					return { success: false, data: null };
				}

				const match = result.data.find(
					(client) => client.email?.trim().toLowerCase() === trimmedEmail,
				);

				return { success: true, data: match ?? null };
			},
			listOrganizationClients: async (organizationId: string) => {
				if (!isValidAdmin || !organizationId) return;

				const profilesPath = FirebaseAPI.firestore.getPath({
					collectionName: "profiles",
					storeId,
					companyId,
				});

				// Primary query: new organizationIds array field
				const result = await FirebaseApi.firestore.listV2<TProfile>({
					collection: profilesPath,
					where: [
						{ name: "storeId", operator: "==", value: store.id },
						{
							name: "organizationIds",
							operator: "array-contains",
							value: organizationId,
						},
					],
					sort: [{ name: "displayName", value: "asc" }],
				});

				// Fallback: also query legacy organizationId field for un-migrated profiles
				const legacyResult = await FirebaseApi.firestore.listV2<TProfile>({
					collection: profilesPath,
					where: [
						{ name: "storeId", operator: "==", value: store.id },
						{
							name: "organizationId",
							operator: "==",
							value: organizationId,
						},
					],
					sort: [{ name: "displayName", value: "asc" }],
				});

				if (!result?.success && !legacyResult?.success) return result;

				// Merge and deduplicate by id
				const allProfiles = [
					...(result?.data ?? []),
					...(legacyResult?.data ?? []),
				];
				const seen = new Set<string>();
				const deduplicated = allProfiles.filter((p) => {
					if (seen.has(p.id)) return false;
					seen.add(p.id);
					return true;
				});

				return { success: true, data: deduplicated };
			},
			createOrganizationClient: async (payload: {
				displayName: string;
				email: string;
				clientType: TProfile["clientType"];
				paymentType: TProfile["paymentType"];
				phoneNumber?: string;
				companyName?: string;
				organizationId: string;
			}) => {
				if (!isValidAdmin || !tenantId) return;

				const profilesPath = FirebaseAPI.firestore.getPath({
					collectionName: "profiles",
					companyId,
					storeId,
				});

				const profileId = FirebaseApi.firestore.generateDocId(profilesPath);
				const now = Date.now();

				const newProfile: TProfile = {
					id: profileId,
					type: "Profile",
					companyId,
					storeId,
					tenantId,
					clientType: payload.clientType,
					displayName: payload.displayName,
					email: payload.email,
					isAnonymous: false,
					createdDate: now,
					lastActivityDate: now,
					paymentType: payload.paymentType,
					phoneNumber: payload.phoneNumber,
					companyName: payload.companyName,
					address: undefined,
					organizationId: payload.organizationId,
					organizationIds: [payload.organizationId],
				};

				const result = await FirebaseApi.firestore.createV2<TProfile>({
					collection: profilesPath,
					doc: newProfile,
				});

				logger({
					message: "create organization client",
					severity: result.success ? "INFO" : "ERROR",
					result,
					organizationId: payload.organizationId,
					profileId,
				});

				return result;
			},
			assignClientToOrganization: async ({
				clientId,
				organizationId,
			}: {
				clientId: string;
				organizationId: string;
			}) => {
				if (!isValidAdmin) return;

				const result = await FirebaseApi.firestore.update<TProfile>(
					clientId,
					{
						organizationId,
						organizationIds: FirebaseApi.firestore.arrayUnion(
							organizationId,
						) as unknown as string[],
						lastActivityDate: Date.now(),
					},
					FirebaseAPI.firestore.getPath({
						collectionName: "profiles",
						storeId,
						companyId,
					}),
				);

				logger({
					message: "assign client to organization",
					severity: result.success ? "INFO" : "ERROR",
					result,
					clientId,
					organizationId,
				});

				return result;
			},
			removeClientFromOrganization: async (
				clientId: string,
				organizationId?: string,
			) => {
				if (!isValidAdmin) return;

				const profilesPath = FirebaseAPI.firestore.getPath({
					collectionName: "profiles",
					storeId,
					companyId,
				});

				// Read profile first to compute remaining orgs for backward compat
				const profileResult = await FirebaseApi.firestore.getV2<TProfile>({
					collection: profilesPath,
					id: clientId,
				});

				const currentOrgIds = profileResult?.data?.organizationIds ?? [];
				const remainingOrgIds = organizationId
					? currentOrgIds.filter((id: string) => id !== organizationId)
					: [];

				const updatePayload: Record<string, unknown> = {
					organizationId: remainingOrgIds[0] ?? null,
					lastActivityDate: Date.now(),
				};

				if (organizationId) {
					updatePayload.organizationIds =
						FirebaseApi.firestore.arrayRemove(
							organizationId,
						) as unknown as string[];
				}

				const result = await FirebaseApi.firestore.update<TProfile>(
					clientId,
					updatePayload as unknown as Partial<TProfile>,
					profilesPath,
				);

				logger({
					message: "remove client from organization",
					severity: result.success ? "INFO" : "ERROR",
					result,
					clientId,
				});

				return result;
			},
			uploadLogo: async ({ logo }: { logo: File }) => {
				if (!isValidAdmin) return;
				try {
					const path = `${companyId}/${storeId}/logo`;
					const fileRef = await FirebaseApi.storage.upload(path, logo);
					const newLogo = { id: path, url: fileRef.url };
					await FirebaseApi.firestore.setV2({
						collection: FirebaseAPI.firestore.systemCollections.stores,
						doc: {
							id: storeId,
							logoUrl: newLogo.url,
						},
					});
					return { success: true, data: newLogo };
				} catch (error) {
					SentryApi.captureException(error);
					return { success: false };
				}
			},
			updateStoreSettings: async (settings: {
				deliveryPrice?: number | null;
				freeDeliveryPrice?: number | null;
				minimumOrder?: number | null;
				isVatIncludedInPrice?: boolean;
				address?: TAddress | null;
			}) => {
				if (!isValidAdmin) return;

				try {
					// Remove null values to unset fields
					const cleanSettings = Object.fromEntries(
						Object.entries(settings).filter(
							([, value]) => value !== null,
						),
					);

					const result = await FirebaseApi.firestore.setV2({
						collection: FirebaseAPI.firestore.systemCollections.stores,
						doc: {
							id: storeId,
							...cleanSettings,
						},
					});

					logger({
						message: "update store settings",
						severity: result.success ? "INFO" : "ERROR",
						settings,
						result,
					});

					return result;
				} catch (error) {
					SentryApi.captureException(error);
					return { success: false };
				}
			},
			/**
			 * Persist the caller's order with refreshed cart totals.
			 *
			 * cart.items is written VERBATIM from order.cart.items — fulfillment flags
			 * (status, substitutedWith) and missing/substituted lines are preserved
			 * exactly as the caller built them.
			 *
			 * Only the aggregate totals (cartDiscount, cartTotal, cartVat) are
			 * recomputed via getCartCost so they reflect the current pricing rules.
			 * This mirrors saveOrder's contract — raw items in, derived totals out —
			 * and ensures that editing a previously-picked order does NOT destroy the
			 * picking record.
			 *
			 * Note: the old per-line finalPrice refresh side-effect is intentionally
			 * dropped. Money-critical consumers (delivery note, payments, invoices)
			 * recompute via getCartCost themselves or read cartTotal directly.
			 */
			async updateOrder({ order }: { order: TOrder }) {
				if (!isValidAdmin) return;

				const cartCost = getCartCost({
					cart: order.cart.items ?? [],
					discounts: [],
					deliveryPrice: order.storeOptions?.deliveryPrice ?? 0,
					freeDeliveryPrice: order.storeOptions?.freeDeliveryPrice ?? 0,
					freeShipping: order.storeOptions?.freeShipping ?? false,
					isVatIncludedInPrice:
						order.storeOptions?.isVatIncludedInPrice ?? false,
				});

				const newOrder: TOrder = {
					...order,
					cart: {
						...order.cart,
						// Preserve the caller's items (including fulfillment flags) verbatim.
						// Do NOT replace with cartCost.items — that would strip status /
						// substitutedWith and drop missing/substituted lines.
						items: order.cart.items,
						cartDiscount: cartCost.discount,
						cartTotal: cartCost.finalCost,
						cartVat: cartCost.vat,
					},
				};

				return FirebaseApi.firestore.setV2<TOrder>({
					collection: FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "orders",
					}),
					doc: newOrder,
				});
			},
			/**
			 * Write the order EXACTLY as built by the caller — NO cart recompute at all.
			 * Use for picking modals where cart.items already carry fulfillment status /
			 * substitutedWith and cart.cartTotal is already the FULFILLED total computed
			 * by the caller. Unlike updateOrder, saveOrder does not touch totals either.
			 */
			async saveOrder({ order }: { order: TOrder }) {
				if (!isValidAdmin) return { success: false as const };
				return FirebaseApi.firestore.setV2<TOrder>({
					collection: FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "orders",
					}),
					doc: {
						...order,
						updatedBy: userId ?? "",
						updatedAt: Date.now(),
					},
				});
			},
			async chargeOrder({ order }: { order: TOrder }) {
				// get transactionId

				await FirebaseApi.api.chargeOrder({
					order: order,
				});
				return { success: true };
				// create token
			},
			async endOrder({ order }: { order: TOrder }) {
				// get transactionId
				if (!isValidAdmin) return;

				return FirebaseApi.firestore.update<TOrder>(
					order.id,
					{
						status: "completed",
						paymentStatus: "completed",
					},
					FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "orders",
					}),
				);
			},
			orderPaid({ order }: { order: TOrder }) {
				if (!isValidAdmin) return;
				mixPanelApi.track("ADMIN_ORDER_PAID", {
					order,
				});
				return FirebaseApi.firestore.update<TOrder>(
					order.id,
					{
						status: "completed",
					},
					FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "orders",
					}),
				);
			},
			orderInDelivery({ order }: { order: TOrder }) {
				if (!isValidAdmin) return;
				mixPanelApi.track("ADMIN_ORDER_DELIVERED", {
					order,
				});
				return FirebaseApi.firestore.update<TOrder>(
					order.id,
					{
						status: "in_delivery",
					},
					FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "orders",
					}),
				);
			},
			orderDelivered({ order }: { order: TOrder }) {
				if (!isValidAdmin) return;
				mixPanelApi.track("ADMIN_ORDER_DELIVERED", {
					order,
				});
				return FirebaseApi.firestore.update<TOrder>(
					order.id,
					{
						status: "delivered",
					},
					FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "orders",
					}),
				);
			},
			orderAccept({ order }: { order: TOrder }) {
				if (!isValidAdmin) return;
				mixPanelApi.track("ADMIN_ORDER_ACCEPT", {
					order,
				});
				return FirebaseApi.firestore.update<TOrder>(
					order.id,
					{
						status: "processing",
					},
					FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "orders",
					}),
				);
			},
			cancelOrder({ order }: { order: TOrder }) {
				if (!isValidAdmin) return;
				mixPanelApi.track("ADMIN_ORDER_CANCEL", {
					order,
				});
				return FirebaseApi.firestore.update<TOrder>(
					order.id,
					{
						status: "cancelled",
						updatedBy: userId ?? "",
						updatedAt: Date.now(),
					},
					FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "orders",
					}),
				);
			},

			async approveOrder({ order }: { order: TOrder }) {
				if (!isValidAdmin) return { success: false as const };

				try {
					const res = await FirebaseApi.api.updateOrder({
						companyId,
						storeId,
						orderId: order.id,
						updates: {
							status: "completed",
						},
					});
					return { success: res.success };
				} catch {
					return { success: false as const };
				}
			},

			category: {
				create: async (category: TCategory) => {
					if (!isValidAdmin) return;

					logger({
						message: `admin try create category`,
						severity: "INFO",
						category,
					});
					updateLoading({
						"admin.category.create": true,
					});

					const res = await FirebaseApi.firestore.setV2({
						collection: FirebaseAPI.firestore.getPath({
							companyId,
							storeId,
							collectionName: "categories",
						}),
						doc: {
							id: "categories",
							categories: FirebaseApi.firestore.arrayUnion(category),
						},
					});

					logger({
						message: `admin ${res.success ? "success" : "fail"}  create category`,
						severity: res.success ? "INFO" : "ALERT",
						data: category ?? {},
					});

					updateLoading({
						"admin.category.create": false,
					});

					return res;
				},
				update: async (
					categories: TCategory[],
					currentCategories: TCategory[],
				) => {
					if (!isValidAdmin) return;

					const changes = diff(currentCategories, categories);

					logger({
						message: `admin try update category`,
						severity: "INFO",
						categories,
						changes,
					});
					updateLoading({
						"admin.category.update": true,
					});

					const res = await FirebaseApi.firestore.setV2({
						collection: FirebaseAPI.firestore.getPath({
							companyId,
							storeId,
							collectionName: "categories",
						}),
						doc: {
							id: "categories",
							categories: categories,
						},
					});

					logger({
						message: `admin ${res.success ? "success" : "fail"}  update category`,
						severity: res.success ? "INFO" : "ALERT",
						data: categories ?? {},
						changes,
					});

					updateLoading({
						"admin.category.update": false,
					});
				},
			},
			// Organization management
			createOrganization: async (organization: TNewOrganization) => {
				if (!isValidAdmin || !companyId || !storeId) return;

				updateLoading({ "admin.createOrganization": true });

				const validation = NewOrganizationSchema.safeParse(organization);
				if (!validation.success) {
					logger({
						message: "create organization",
						severity: "ERROR",
						error: validation.error,
						organization,
					});
					return { success: false, error: validation.error };
				}

				const doc = validation.data;

				const result = await FirebaseApi.firestore.createV2<
					TNewOrganization & { id?: string }
				>({
					collection: FirebaseAPI.firestore.getPath({
						storeId,
						companyId,
						collectionName: "organizations",
					}),
					doc: doc,
				});
				updateLoading({ "admin.createOrganization": false });

				logger({
					message: "create organization",
					severity: result.success ? "INFO" : "ERROR",
					result,
					organization,
				});

				return {
					success: true,
					data: { ...organization, id: result.docId },
				};
			},
			updateOrganization: async (organization: TOrganization) => {
				if (!isValidAdmin || !companyId || !storeId) return;

				const result = await FirebaseApi.firestore.update<TOrganization>(
					organization.id,
					organization,
					FirebaseAPI.firestore.getPath({
						storeId,
						companyId,
						collectionName: "organizations",
					}),
				);
				logger({
					message: "update organization",
					severity: result.success ? "INFO" : "ERROR",
					result,
					organization,
				});

				return result;
			},
			deleteOrganization: async (organizationId: string) => {
				if (!isValidAdmin || !companyId || !storeId) return;

				updateLoading({ "admin.deleteOrganization": true });
				const result = await FirebaseApi.firestore.remove({
					id: organizationId,
					collectionName: FirebaseAPI.firestore.getPath({
						storeId,
						companyId,
						collectionName: "organizations",
					}),
				});
				updateLoading({ "admin.deleteOrganization": false });

				logger({
					message: "delete organization",
					severity: result.success ? "INFO" : "ERROR",
					result,
					organizationId,
				});

				return result;
			},
			listOrganizations: async () => {
				if (!isValidAdmin || !companyId || !storeId) return;

				updateLoading({ "admin.listOrganizations": true });
				const result = await FirebaseApi.firestore.listV2<TOrganization>({
					collection: FirebaseAPI.firestore.getPath({
						storeId,
						companyId,
						collectionName: "organizations",
					}),
					sort: [{ name: "name", value: "asc" }],
				});
				updateLoading({ "admin.listOrganizations": false });

				return result;
			},
			listProducts: async () => {
				if (!isValidAdmin || !companyId || !storeId) return;

				const result = await FirebaseApi.firestore.listV2<TProduct>({
					collection: FirebaseAPI.firestore.getPath({
						storeId,
						companyId,
						collectionName: "products",
					}),
					sort: [{ name: "sku" as any, value: "asc" }],
				});

				return result;
			},
			// Organization Group management
			createOrganizationGroup: async (
				organizationGroup: TNewOrganizationGroup,
			) => {
				if (!isValidAdmin || !companyId || !storeId) return;

				updateLoading({ "admin.createOrganizationGroup": true });

				const validation =
					NewOrganizationGroupSchema.safeParse(organizationGroup);
				if (!validation.success) {
					logger({
						message: "create organization group",
						severity: "ERROR",
						error: validation.error,
						organizationGroup,
					});
					return { success: false, error: validation.error };
				}

				const doc = validation.data;

				const result = await FirebaseApi.firestore.createV2<
					TNewOrganizationGroup & { id?: string }
				>({
					collection: FirebaseAPI.firestore.getPath({
						storeId,
						companyId,
						collectionName: "organizationGroups",
					}),
					doc: doc,
				});
				updateLoading({ "admin.createOrganizationGroup": false });

				logger({
					message: "create organization group",
					severity: result.success ? "INFO" : "ERROR",
					result,
					organizationGroup,
				});

				return result;
			},
			updateOrganizationGroup: async (
				organizationGroup: TOrganizationGroup,
			) => {
				if (!isValidAdmin || !companyId || !storeId) return;

				const result =
					await FirebaseApi.firestore.update<TOrganizationGroup>(
						organizationGroup.id,
						organizationGroup,
						FirebaseAPI.firestore.getPath({
							storeId,
							companyId,
							collectionName: "organizationGroups",
						}),
					);
				logger({
					message: "update organization group",
					severity: result.success ? "INFO" : "ERROR",
					result,
					organizationGroup,
				});

				return result;
			},
			deleteOrganizationGroup: async (organizationGroupId: string) => {
				if (!isValidAdmin || !companyId || !storeId) return;

				updateLoading({ "admin.deleteOrganizationGroup": true });
				const result = await FirebaseApi.firestore.remove({
					id: organizationGroupId,
					collectionName: FirebaseAPI.firestore.getPath({
						storeId,
						companyId,
						collectionName: "organizationGroups",
					}),
				});
				updateLoading({ "admin.deleteOrganizationGroup": false });

				logger({
					message: "delete organization group",
					severity: result.success ? "INFO" : "ERROR",
					result,
					organizationGroupId,
				});

				return result;
			},
			listOrganizationGroups: async () => {
				if (!isValidAdmin || !companyId || !storeId) return;

				updateLoading({ "admin.listOrganizationGroups": true });
				const result =
					await FirebaseApi.firestore.listV2<TOrganizationGroup>({
						collection: FirebaseAPI.firestore.getPath({
							storeId,
							companyId,
							collectionName: "organizationGroups",
						}),
						sort: [{ name: "name", value: "asc" }],
					});
				updateLoading({ "admin.listOrganizationGroups": false });

				return result;
			},
			// Supplier management
			createSupplier: async (supplier: TNewSupplier) => {
				if (!isValidAdmin || !companyId || !storeId) return;

				updateLoading({ "admin.createSupplier": true });

				const validation = NewSupplierSchema.safeParse(supplier);
				if (!validation.success) {
					logger({
						message: "create supplier",
						severity: "ERROR",
						error: validation.error,
						supplier,
					});
					return { success: false, error: validation.error };
				}

				const doc = validation.data;

				const result = await FirebaseApi.firestore.createV2<
					TNewSupplier & { id?: string }
				>({
					collection: FirebaseAPI.firestore.getPath({
						storeId,
						companyId,
						collectionName: "suppliers",
					}),
					doc: doc,
				});
				updateLoading({ "admin.createSupplier": false });

				logger({
					message: "create supplier",
					severity: result.success ? "INFO" : "ERROR",
					result,
					supplier,
				});

				return result;
			},
			updateSupplier: async (supplier: TSupplier) => {
				if (!isValidAdmin || !companyId || !storeId) return;

				updateLoading({ "admin.updateSupplier": true });

				const validation = SupplierSchema.safeParse(supplier);
				if (!validation.success) {
					logger({
						message: "update supplier",
						severity: "ERROR",
						error: validation.error,
						supplier,
					});
					updateLoading({ "admin.updateSupplier": false });
					return { success: false, error: validation.error };
				}

				const result = await FirebaseApi.firestore.update<TSupplier>(
					supplier.id,
					supplier,
					FirebaseAPI.firestore.getPath({
						storeId,
						companyId,
						collectionName: "suppliers",
					}),
				);
				updateLoading({ "admin.updateSupplier": false });

				logger({
					message: "update supplier",
					severity: result.success ? "INFO" : "ERROR",
					result,
					supplier,
				});

				return result;
			},
			deleteSupplier: async (supplierId: string) => {
				if (!isValidAdmin || !companyId || !storeId) return;

				updateLoading({ "admin.deleteSupplier": true });
				const result = await FirebaseApi.firestore.remove({
					id: supplierId,
					collectionName: FirebaseAPI.firestore.getPath({
						storeId,
						companyId,
						collectionName: "suppliers",
					}),
				});
				updateLoading({ "admin.deleteSupplier": false });

				logger({
					message: "delete supplier",
					severity: result.success ? "INFO" : "ERROR",
					result,
					supplierId,
				});

				return result;
			},
			listSuppliers: async () => {
				if (!isValidAdmin || !companyId || !storeId) return;

				updateLoading({ "admin.listSuppliers": true });
				const result = await FirebaseApi.firestore.listV2<TSupplier>({
					collection: FirebaseAPI.firestore.getPath({
						storeId,
						companyId,
						collectionName: "suppliers",
					}),
					sort: [{ name: "name", value: "asc" }],
				});
				updateLoading({ "admin.listSuppliers": false });

				return result;
			},
			getChatbotConfig: async () => {
				if (!isValidAdmin) return { success: false as const, data: null };
				return FirebaseApi.firestore.getV2<{
					storeContext: string;
					updatedAt: number;
				}>({
					collection: `STORES/${storeId}/private`,
					id: "chatbotConfig",
				});
			},
			updateChatbotConfig: async ({
				storeContext,
			}: {
				storeContext: string;
			}) => {
				if (!isValidAdmin) return { success: false as const };
				return FirebaseApi.firestore.setV2({
					collection: `STORES/${storeId}/private`,
					doc: {
						id: "chatbotConfig",
						storeContext: storeContext.trim().slice(0, 3000),
						updatedAt: Date.now(),
					},
				});
			},
			// Home page "featured products" strip — owner-curated product ids.
			// Stored tenant-scoped so the storefront can read it client-side.
			getFeaturedProducts: async () => {
				if (!isValidAdmin || !companyId || !storeId)
					return { success: false as const, data: null };
				return FirebaseApi.firestore.getV2<{
					productIds: string[];
					updatedAt: number;
				}>({
					collection: FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "settings",
					}),
					id: "homeFeatured",
				});
			},
			updateFeaturedProducts: async (productIds: string[]) => {
				if (!isValidAdmin || !companyId || !storeId)
					return { success: false as const };
				return FirebaseApi.firestore.setV2({
					collection: FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "settings",
					}),
					doc: {
						id: "homeFeatured",
						productIds: productIds.slice(0, 6),
						updatedAt: Date.now(),
					},
				});
			},
		},
		system: {
			getDiscounts: async () => {
				if (!isValidUser)
					return {
						success: false,
						data: [],
						error: new Error("invalid user"),
					};

				const response = await FirebaseApi.firestore.listV2<TDiscount>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "discounts",
						companyId,
						storeId,
					}),
				});

				logger({ message: "fetch discounts", severity: "DEBUG", response });

				return response;
			},
			getProductById: async ({ id }: { id: TProduct["id"] }) => {
				if (!isValid) return;

				const response = await FirebaseApi.firestore.getV2<TProduct>({
					collection: FirebaseAPI.firestore.getPath({
						companyId: company.id,
						storeId: store.id,
						collectionName: "products",
					}),
					id,
				});

				logger({
					message: "system.getProductById",
					severity: "DEBUG",
					response,
				});

				return response;
			},
			getProductBySku: async ({ sku }: { sku: TProduct["sku"] }) => {
				if (!isValid) return;

				// Products are normally stored with doc id == sku, but legacy /
				// imported products (or products whose barcode changed after
				// creation) may have a different doc id while still holding the
				// barcode in their `sku` field. Query the `sku` field so those
				// products are found too — always scoped to the current tenant.
				const response = await FirebaseApi.firestore.listV2<TProduct>({
					collection: FirebaseAPI.firestore.getPath({
						companyId: company.id,
						storeId: store.id,
						collectionName: "products",
					}),
					where: [{ name: "sku", value: sku, operator: "==" }],
					limitCount: 1,
				});

				logger({
					message: "system.getProductBySku",
					severity: "DEBUG",
					response,
				});

				if (!response?.success) {
					return { success: false as const, data: undefined };
				}

				// success + no data == barcode is free; success:false == lookup failed.
				return { success: true as const, data: response.data[0] as TProduct | undefined };
			},
			getUserOrders: async () => {
				if (!isValidStoreData || !isValidUser) return;

				return FirebaseApi.firestore.listV2<TOrder>({
					collection: FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "orders",
					}),
					where: [
						{
							name: "storeId",
							operator: "==",
							value: store.id,
						},
						{
							name: "userId",
							operator: "==",
							value: user.uid,
						},
					],
				});
			},

			async getStoreCategories() {
				if (!isValid) return;

				const res = await FirebaseApi.firestore.getV2<{
					categories: TCategory[];
				}>({
					collection: FirebaseAPI.firestore.getPath({
						companyId: company.id,
						storeId: store.id,
						collectionName: "categories",
					}),
					id: "categories",
				});

				return res;
			},

			auth: {
				logout: async () => {
					if (!isValid) return;

					mixPanelApi.track("AUTH_USER_LOGOUT", {
						storeId: store.id,
						companyId: company.id,
						userId: user.uid,
						userEmail: user.email,
						tenantId: store.tenantId,
					});
					await FirebaseApi.auth.logout();
					navigate({
						to: "store",
					});
					actions.dispatch(
						actions.cart.setCart({ cart: null, isReady: false }),
					);
					actions.dispatch(actions.orders.setOrders([]));
					actions.dispatch(
						actions.favoriteProducts.setFavoriteProducts([]),
					);
					actions.dispatch(actions.profile.setProfile(null));
				},
			},
			categories: {
				get: async () => {
					if (!isValid) return [];
					const res = await FirebaseApi.firestore.get<{
						categories: TCategory[];
					}>(store.id, "categories");

					return res?.data?.categories ?? [];
				},
			},
			async onOrderPaid(payment: any) {
				if (!isValidUser) {
					return;
				}

				if (!payment?.Order || !payment?.Id) {
					return;
				}

				// Every HYP result — a J5 hold (CCode 700) or a direct charge (CCode 0)
				// — becomes a ledger transaction through the server: it re-verifies the
				// result with HYP (VERIFY) and is idempotent on the HYP Id. Everything
				// downstream (order paid, AR, the invoice-receipt) runs off that
				// transaction. One call, no retries: a failure is logged loudly, and
				// reloading this page runs it again safely.
				const recorded = await FirebaseApi.api.recordHypJ5Auth({
					companyId,
					storeId,
					Id: payment.Id,
					CCode: payment.CCode,
					Amount: payment.Amount,
					Order: payment.Order,
					Masof: payment.Masof,
					UID: payment.UID,
					ACode: payment.ACode,
					Sign: payment.Sign,
					reference: { type: "order", id: payment.Order },
					rawResponse: payment,
				});
				const recordedOk = recorded.success && recorded.data?.success === true;
				logger({
					message: recordedOk
						? "client record hyp result"
						: "client record hyp result FAILED — no ledger transaction for this payment",
					severity: recordedOk ? "INFO" : "ERROR",
					Id: payment.Id,
					Order: payment.Order,
					CCode: payment.CCode,
					result: recorded,
					userId: user.uid,
				});

				// Legacy order status write. The ledger's onTransactionPostedMarkOrderPaid
				// subscriber writes the same value from the transaction; kept until the
				// ledger path has been seen doing it in production.
				await FirebaseApi.firestore.setV2<Partial<TOrder>>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "orders",
						companyId,
						storeId,
					}),
					doc: {
						id: payment.Order,
						paymentStatus:
							store?.paymentType === "external"
								? "external"
								: payment.CCode === "0"
									? "completed" // CCode 0 = card actually charged → mark paid
									: "pending_j5", // CCode 700 = J5 hold → awaiting capture
						originalAmount: payment.Amount,
						actualAmount: payment.Amount,
					},
				});
				// Raw HYP result, for troubleshooting ONLY — nothing in the application
				// reads it. Create-only: a second result for the same order is logged,
				// never silently dropped.
				const stored = await FirebaseApi.firestore.createV2({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "payments",
						companyId,
						storeId,
					}),
					doc: {
						id: payment.Order,
						payment,
						date: Date.now(),
						storeId: store.id,
						companyId: companyId,
						userId: user.uid,
					},
				});
				if (!stored?.success) {
					logger({
						message:
							"payments/{orderId} already exists — a second HYP result for this order was not stored in the debug doc",
						severity: "ERROR",
						Id: payment.Id,
						Order: payment.Order,
						CCode: payment.CCode,
						userId: user.uid,
					});
				}
			},
			async onPaymentFailed(payment: Record<string, string>) {
				if (!isValidUser) {
					return;
				}

				if (!payment.Order) {
					return;
				}

				await FirebaseApi.firestore.setV2<Partial<TOrder>>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "orders",
						companyId,
						storeId,
					}),
					doc: {
						id: payment.Order,
						paymentStatus: "failed",
					},
				});
			},
		},
		user: {
			permissions: {
				canCancelOrder({ order }: { order: TOrder }) {
					if (order.status === "pending") return true;
					return false;
				},
			},
			subscriptions: {
				profileSubscribe: () => {
					if (!isValidUser || user?.isAnonymous === true) return;

					const unsubscribe =
						FirebaseApi.firestore.subscribeDocV2<TProfile>({
							collection: FirebaseAPI.firestore.getPath({
								collectionName: "profiles",
								storeId,
								companyId,
							}),
							id: user.uid,
							callback(profile) {
								actions.dispatch(actions.profile.setProfile(profile));
							},
						});
					return unsubscribe;
				},
				favoriteProductsSubscribe: (
					callback: (favoriteProducts: TFavoriteProduct[]) => void,
				) => {
					if (
						!user ||
						!company ||
						!store ||
						user.isAnonymous ||
						!companyId ||
						!storeId
					)
						return;

					return FirebaseApi.firestore.subscribeList({
						callback,
						collection: FirebaseAPI.firestore.getPath({
							collectionName: "favorite-products",
							companyId,
							storeId,
						}),
						where: [
							{ name: "userId", operator: "==", value: user.uid },
							{ name: "storeId", operator: "==", value: store.id },
							{ name: "companyId", operator: "==", value: company.id },
						],
					});
				},
			},
			getProfileOrganization: async () => {
				if (!isValidUser) return;

				const orgCollection = FirebaseAPI.firestore.getPath({
					collectionName: "organizations",
					storeId,
					companyId,
				});

				// Build the list of org IDs to fetch — prefer organizationIds array, fall back to legacy organizationId
				const orgIds: string[] = [];
				if (
					profile?.organizationIds &&
					profile.organizationIds.length > 0
				) {
					orgIds.push(...profile.organizationIds);
				} else if (profile?.organizationId) {
					orgIds.push(profile.organizationId);
				}

				if (orgIds.length === 0)
					return { success: true, data: [] as TOrganization[] };

				const results = await Promise.all(
					orgIds.map((id) =>
						FirebaseApi.firestore.getV2<TOrganization>({
							collection: orgCollection,
							id,
						}),
					),
				);

				const organizations = results
					.filter((r) => r.success && r.data)
					.map((r) => r.data as TOrganization);

				return { success: true, data: organizations };
			},
			getOrder: async ({ id }: { id: string }) => {
				if (!isValidUser)
					return {
						success: false,
						data: null,
						error: new Error("invalid user"),
					};

				return FirebaseApi.firestore.getV2<TOrder>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "orders",
						companyId,
						storeId,
					}),
					id: id,
				});
			},

			async addProductToFavorite({ product }: { product: TProduct }) {
				if (
					!product ||
					!user ||
					!company ||
					!store ||
					user.isAnonymous ||
					!companyId ||
					!storeId
				)
					return;
				if (user.isAnonymous && !allowAnonymousClients) {
					modalApi.openModal("authModal");
					return;
				}
				return await FirebaseApi.firestore.setV2<any>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "favorite-products",
						companyId,
						storeId,
					}),
					doc: {
						productId: product.id,
						userId: user.uid,
						storeId: store.id,
						companyId: company.id,
					},
				});
			},
			async removeProductToFavorite({ id }: { id: string }) {
				if (
					!id ||
					!user ||
					!company ||
					!store ||
					user.isAnonymous ||
					!companyId ||
					!storeId
				)
					return;
				if (user.isAnonymous && !allowAnonymousClients) {
					modalApi.openModal("authModal");
					return;
				}
				return await FirebaseApi.firestore.remove({
					collectionName: FirebaseAPI.firestore.getPath({
						collectionName: "favorite-products",
						companyId,
						storeId,
					}),
					id: id,
				});
			},
			async profileUpdate({ newProfile }: { newProfile: TProfile }) {
				try {
					if (!user || !store || !profile) return;
					if (user.isAnonymous && !allowAnonymousClients) {
						modalApi.openModal("authModal");
						return;
					}

					if (!isValidStoreData) return;

					await FirebaseApi.firestore.update(
						user.uid,
						newProfile,
						FirebaseAPI.firestore.getPath({
							collectionName: "profiles",
							storeId,
							companyId,
						}),
					);
					logger({
						message: "profile updated",
						severity: "INFO",
						profile,
						newProfile,
					});
					return { success: true, data: profile };
				} catch (error) {
					return { success: false, error };
				}
			},
			async createPaymentLink({
				order,
				isJ5 = false,
			}: {
				order: TOrder;
				isJ5?: boolean;
			}) {
				if (!user || !store || !order) return;
				if (user.isAnonymous && !allowAnonymousClients) {
					modalApi.openModal("authModal");
					return;
				}

				// New flow: the server loads the order and signs the HYP form (amount and
				// ownership are enforced server-side). The order must already be written to
				// Firestore — checkout/user-orders write it before calling this.
				const payment = await FirebaseApi.api.createHypCheckoutPayment({
					orderId: order.id,
					companyId: order.companyId,
					storeId: order.storeId,
					isJ5,
				});
				logger({
					message: "client create payment link",
					severity: "INFO",
					payment,
					userId: user.uid,
				});
				return payment;
			},
			async cancelOrder({ order }: { order: TOrder }) {
				if (!isValidUser) return;
				// mixPanelApi.track("ADMIN_ORDER_ACCEPT", {
				// 	order,
				// });
				return FirebaseApi.firestore.update<TOrder>(
					order.id,
					{
						status: "cancelled",
					},
					FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "orders",
					}),
				);
			},
			async createCartFromOrder({ order }: { order: TOrder }) {
				if (!isValidUser) return;
				if (user.isAnonymous && !allowAnonymousClients) {
					modalApi.openModal("authModal");
					return;
				}

				// mark currentCart as draft
				if (cart?.id) {
					await FirebaseApi.firestore.setV2<TCart>({
						collection: FirebaseAPI.firestore.getPath({
							companyId,
							storeId,
							collectionName: "cart",
						}),
						doc: { ...cart, status: "draft" },
					});
				}

				await FirebaseApi.firestore.setV2({
					collection: FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "cart",
					}),
					doc: {
						id: FirebaseApi.firestore.generateDocId("cart"),
						status: "active",
						items: order.cart.items,
						companyId: company?.id,
						storeId: store.id,
						type: "Cart",
						userId: user.uid,
					},
				});

				logger({
					severity: "INFO",
					message: "user create cart from order",
					order,
				});

				navigate({
					to: "store.catalog",
				});
			},

			async addItemToCart({ product }: { product: TProduct }) {
				if (!isValidUser) {
					return;
				}

				if (user.isAnonymous && !allowAnonymousClients) {
					modalApi.openModal("authModal");
					return;
				}

				logger({
					message: "user add item to cart",
					severity: "INFO",
				});

				const cartItems = cart?.items ?? [];

				const validation = ProductSchema.safeParse(product);
				if (!validation.success) {
					logger({
						message: `add item to cart - invalid product data`,
						severity: "ERROR",
						product,
						error: validation.error,
					});
					return;
				}
				const safeProduct = validation.data;

				const isProductInCart = productInCart(cart, safeProduct);

				if (isProductInCart) {
					const items = structuredClone(cartItems ?? []);
					const productIndex = (cartItems ?? []).findIndex(
						(cartItem) => cartItem.product.id === product?.id,
					);
					items[productIndex].amount += 1;
					FirebaseApi.firestore.setV2<TCart>({
						collection: FirebaseAPI.firestore.getPath({
							companyId,
							storeId,
							collectionName: "cart",
						}),
						doc: {
							...actualCart,
							items,
						},
					});
				} else {
					const items = [
						...cartItems,
						{
							amount: 1,
							product: safeProduct,
						},
					];

					FirebaseApi.firestore.setV2<TCart>({
						collection: FirebaseAPI.firestore.getPath({
							companyId,
							storeId,
							collectionName: "cart",
						}),
						doc: {
							...actualCart,
							items,
						},
					});
				}
				mixPanelApi.track("USER_ADD_ITEM_TO_CART", {
					productId: product.id,
					productName: product.name[0].value, //todo get correct lang
					storeId: store.id,
					storeName: store.name,
					companyId: companyId,
					companyName: company?.name,
				});

				// Balasi storefront: surface the cart drawer when an item is added so
				// the customer sees their cart pop up (scoped to balasi/tester only).
				// On desktop (≥ xl / 1280px) a docked cart sidebar is already visible,
				// so skip the drawer there — only auto-open below xl where there is no
				// docked sidebar, otherwise it dims the whole page on every add.
				const isBalasiStorefront =
					store?.id === "balasistore_store" ||
					store?.id === "tester_store";
				const dockedCartVisible =
					typeof window !== "undefined" &&
					window.matchMedia("(min-width: 1280px)").matches;
				if (isBalasiStorefront && !dockedCartVisible) {
					actions.dispatch(actions.ui.openCartDrawer());
				}
			},
			async removeItemFromCart({ product }: { product: TProduct }) {
				if (!isValidUser) {
					return;
				}
				if (user.isAnonymous && !allowAnonymousClients) {
					modalApi.openModal("authModal");
					return;
				}

				logger({
					message: "user remove item from cart",
					severity: "INFO",
				});

				const cartItems = cart?.items ?? [];

				const productIndex = cartItems.findIndex(
					(cartItem) => cartItem.product.id === product.id,
				);
				const productInCart = productIndex !== -1;
				if (!productInCart) return;

				const items = structuredClone(cartItems);

				if (items[productIndex].amount > 1) {
					items[productIndex].amount -= 1;
					FirebaseApi.firestore.setV2<TCart>({
						collection: FirebaseAPI.firestore.getPath({
							companyId,
							storeId,
							collectionName: "cart",
						}),
						doc: {
							...actualCart,
							items,
						},
					});
					mixPanelApi.track("USER_REMOVE_ITEM_FROM_CART", {
						productId: product.id,
						productName: product.name[0].value, //todo get correct lang
					});

					return;
				}
				items.splice(productIndex, 1);

				FirebaseApi.firestore.setV2<TCart>({
					collection: FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "cart",
					}),
					doc: {
						...actualCart,
						items,
					},
				});
				mixPanelApi.track("USER_REMOVE_ITEM_FROM_CART", {
					productId: product.id,
					productName: product.name[0].value, //todo get correct lang
				});
			},
			async updateCartItemAmount({
				product,
				amount,
			}: {
				product: TProduct;
				amount: number;
			}) {
				if (!isValidUser) {
					return;
				}

				if (user.isAnonymous && !allowAnonymousClients) {
					modalApi.openModal("authModal");
					return;
				}

				logger({
					message: "user update cart item amount",
					severity: "INFO",
				});

				const cartItems = cart?.items ?? [];

				const productIndex = cartItems.findIndex(
					(cartItem) => cartItem.product.id === product.id,
				);
				const productInCart = productIndex !== -1;
				if (!productInCart) return;

				const items = structuredClone(cartItems);

				if (amount <= 0) {
					// Remove item if amount is 0 or negative
					items.splice(productIndex, 1);
				} else {
					// Update amount
					items[productIndex].amount = amount;
				}

				FirebaseApi.firestore.setV2<TCart>({
					collection: FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "cart",
					}),
					doc: {
						...actualCart,
						items,
					},
				});

				// Update local state
				actions.dispatch(
					actions.cart.updateItemAmount({ product, amount }),
				);

				mixPanelApi.track("USER_UPDATE_CART_ITEM_AMOUNT", {
					productId: product.id,
					productName: product.name[0].value,
					amount,
				});
			},
			async clearCart() {
				if (!isValidUser) {
					return;
				}

				if (user.isAnonymous && !allowAnonymousClients) {
					modalApi.openModal("authModal");
					return;
				}

				logger({
					message: "user clear cart",
					severity: "INFO",
				});

				FirebaseApi.firestore.setV2<TCart>({
					collection: FirebaseAPI.firestore.getPath({
						companyId,
						storeId,
						collectionName: "cart",
					}),
					doc: {
						...actualCart,
						items: [],
					},
				});

				// Update local state
				actions.dispatch(actions.cart.clear());

				mixPanelApi.track("USER_CLEAR_CART", {
					storeId: store.id,
					storeName: store.name,
					companyId: companyId,
					companyName: company?.name,
				});
			},
		},
		superAdmin: {
			getAllStores: async () => {
				const res = await FirebaseApi.firestore.listV2({
					collection: "stores",
				});
				return res;
			},
		},
		auth: {
			signup: async (newUser: {
				email: string;
				password: string;
				fullName: string;
				companyName?: string;
			}) => {
				if (!isValidStoreData) return;

				mixPanelApi.track("AUTH_USER_SIGNUP", {
					storeId: store.id,
					companyId: companyId,
					userEmail: newUser.email,
					tenantId: store.tenantId,
				});

				const profile: TProfile = {
					id: "",
					companyId: store.companyId,
					storeId: store.id,
					tenantId: store.tenantId,
					displayName: newUser.fullName,
					companyName: newUser.companyName ?? "",
					email: newUser.email,
					isAnonymous: false,
					phoneNumber: "",
					address: {
						apartmentEnterNumber: "",
						apartmentNumber: "",
						city: "",
						country: "",
						floor: "",
						street: "",
						streetNumber: "",
					},
					clientType: newUser.companyName ? "company" : "user",
					createdDate: Date.now(),
					lastActivityDate: Date.now(),
					type: "Profile",
					paymentType: "j5",
				};

				const res = await FirebaseApi.auth.createUser(
					newUser.email,
					newUser.password,
				);
				if (!res.success) {
					logger({
						severity: "CRITICAL",
						message: `fail sign up`,
						...res,
					});
					return res;
				}

				profile.id = res.user?.uid ?? "";

				const newProfile = await FirebaseApi.firestore.setV2<
					Partial<TProfile>
				>({
					collection: FirebaseAPI.firestore.getPath({
						collectionName: "profiles",
						companyId,
						storeId,
					}),
					doc: profile,
				});

				logger({
					severity: "INFO",
					message: "new user sign up",
					userId: res.user?.uid,
					userEmail: newUser.email,
					profile,
				});

				return { user: res.user, profile: newProfile, success: true };
			},
			login: async (data: { email: string; password: string }) => {
				if (!isValidStoreData) return;

				mixPanelApi.track("AUTH_USER_LOGIN", {
					storeId: store.id,
					companyId: companyId,
					userEmail: data.email,
					tenantId: store.tenantId,
				});
				return await FirebaseApi.auth.login(data.email, data.password);
			},

			resetPassword: async (data: { email: string }) => {
				if (!isValidStoreData) return;

				mixPanelApi.track("AUTH_USER_RESET_PASSWORD", {
					storeId: store.id,
					companyId: companyId,
					userEmail: data.email,
					tenantId: store.tenantId,
				});
				return await FirebaseApi.auth.resetPassword(data.email);
			},

			logout: async () => {
				if (!isValid) return;

				mixPanelApi.track("AUTH_USER_LOGOUT", {
					storeId: store.id,
					companyId: company.id,
					userId: user.uid,
					userEmail: user.email,
					tenantId: store.tenantId,
				});
				await FirebaseApi.auth.logout();
				navigate({
					to: "store",
				});
				actions.dispatch(
					actions.cart.setCart({ cart: null, isReady: false }),
				);
				actions.dispatch(actions.orders.setOrders([]));
				actions.dispatch(actions.favoriteProducts.setFavoriteProducts([]));
				actions.dispatch(actions.profile.setProfile(null));
			},
		},
		chatbot: {
			sendMessage: async (
				prompt: string,
				history: { role: "user" | "bot"; text: string }[] = [],
			) => {
				return await FirebaseApi.api.chatbotChat(prompt, history, {
					cartId: cart?.id ?? "",
					companyId,
					storeId,
				});
			},
		},
	};

	return { ...api, loading };
};
